import React, { useState } from 'react';
import { Linkedin, Github, ArrowUp, Eye, Copy, Check } from 'lucide-react';
import { profile } from '../mock';

const maskValue = (v) => v.split('').map(c => /[\s@.+\-]/.test(c) ? c : '●').join('');
const DECODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const MaskedEmail = ({ value }) => {
  const [revealed, setRevealed] = useState(false);
  const [display, setDisplay] = useState(() => maskValue(value));
  const [copied, setCopied] = useState(false);

  const decode = () => {
    if (revealed) return;
    let step = 0;
    const iv = setInterval(() => {
      setDisplay(value.split('').map((char, i) => {
        if (/[\s@.+\-]/.test(char)) return char;
        if (i < step) return char;
        return DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)];
      }).join(''));
      step++;
      if (step > value.length) { clearInterval(iv); setDisplay(value); setRevealed(true); }
    }, 22);
  };

  const copy = (e) => {
    e.preventDefault();
    try { navigator.clipboard?.writeText(value); } catch (_) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className={`mono text-sm tracking-wider transition-colors ${revealed ? 'text-slate-200' : 'text-slate-500 select-none'}`}>
        {display}
      </span>
      {!revealed ? (
        <button onClick={decode}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-white/10 text-slate-500 text-[9px] mono uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">
          <Eye size={9} /> reveal
        </button>
      ) : (
        <button onClick={copy}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-500/30 text-emerald-400 text-[9px] mono uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-colors">
          {copied ? <><Check size={9} /> copied</> : <><Copy size={9} /> copy</>}
        </button>
      )}
    </span>
  );
};

const Footer = () => {
  const top = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="relative bg-[#0a0807] text-slate-300 overflow-hidden border-t border-white/5">
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="relative w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">PP</span>
              </div>
              <div>
                <div className="text-white font-semibold">{profile.name}</div>
                <div className="text-xs mono uppercase tracking-widest text-primary">{profile.role}</div>
              </div>
            </div>
            <p className="text-slate-500 max-w-md leading-relaxed text-sm">
              Available for senior architecture, advisory and training engagements across Azure, hybrid cloud and DevOps.
            </p>
          </div>

          {/* Navigate */}
          <div className="lg:col-span-3">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-600 mb-4">Navigate</div>
            <ul className="space-y-2.5">
              {['About', 'Skills', 'Experience', 'Projects', 'Certifications', 'Contact'].map((l) => (
                <li key={l}>
                  <button
                    onClick={() => {
                      const el = document.getElementById(l.toLowerCase());
                      if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
                    }}
                    className="text-slate-400 hover:text-primary transition-colors text-sm"
                  >
                    {l}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="lg:col-span-3">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-600 mb-4">Connect</div>
            <ul className="space-y-2.5">
              <li><MaskedEmail value={profile.email} /></li>
              <li className="text-slate-500 text-sm">{profile.location}</li>
            </ul>
            <div className="flex gap-2 mt-5">
              <a href={profile.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 hover:border-primary hover:bg-primary flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300">
                <Linkedin size={15} />
              </a>
              <a href={profile.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 hover:border-primary hover:bg-primary flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300">
                <Github size={15} />
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-white/5">
          <p className="text-xs mono text-slate-600">
            © {new Date().getFullYear()} {profile.name}. Architected with care.
          </p>
          <button
            onClick={top}
            className="group flex items-center gap-2 text-xs mono uppercase tracking-widest text-slate-500 hover:text-primary transition-colors"
          >
            Back to top
            <span className="w-7 h-7 rounded-full bg-white/5 border border-white/8 group-hover:border-primary group-hover:bg-primary flex items-center justify-center transition-all duration-300">
              <ArrowUp size={12} />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
